/**
 * Authentication utilities for user state management
 */

import { getApiConfig } from './env'

export interface AuthUser {
  id: string
  email: string
  firstName?: string
  lastName?: string
  displayName?: string
  roles?: string[]
}

export interface AuthState {
  isLoggedIn: boolean
  user: AuthUser | null
  token: string | null
  refreshToken: string | null
}

export interface SilentAuthResponse {
  authenticated: boolean
  authenticatedForPII?: boolean
  accessToken?: string
  isMinistryPartner?: boolean
  email?: string
  id?: string
  ligId?: string
  firstName?: string
  lastName?: string
  partnerAccess?: boolean
  error?: string
}

export interface SilentAuthOptions {
  endpoint?: string
  abortSignal?: AbortSignal
}

/**
 * Configuration for auth API
 */
const AUTH_CONFIG = getApiConfig()

/**
 * Check if user is currently authenticated
 */
export const checkAuthStatus = async (): Promise<AuthState> => {
  try {
    const token = getStoredToken()

    if (!token) {
      return {
        isLoggedIn: false,
        user: null,
        token: null,
        refreshToken: null
      }
    }

    const response = await fetch(`${AUTH_CONFIG.authUrl}/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      },
    })

    if (!response.ok) {
      // Token might be expired, clear it
      clearStoredTokens()
      throw new Error(`Auth check failed: ${response.statusText}`)
    }

    const userData = await response.json()

    return {
      isLoggedIn: true,
      user: userData,
      token,
      refreshToken: getStoredRefreshToken()
    }
  } catch (error) {
    console.error('Error checking auth status:', error)
    clearStoredTokens()
    return {
      isLoggedIn: false,
      user: null,
      token: null,
      refreshToken: null
    }
  }
}

/**
 * Login user with credentials
 */
export const loginUser = async (email: string, password: string): Promise<AuthState> => {
  try {
    const response = await fetch(`${AUTH_CONFIG.authUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({ email, password })
    })

    if (!response.ok) {
      throw new Error(`Login failed: ${response.statusText}`)
    }

    const data = await response.json()

    // Store tokens securely
    storeTokens(data.accessToken, data.refreshToken)

    return {
      isLoggedIn: true,
      user: data.user,
      token: data.accessToken,
      refreshToken: data.refreshToken
    }
  } catch (error) {
    console.error('Login error:', error)
    throw error
  }
}

/**
 * Logout user and clear tokens
 */
export const logoutUser = async (): Promise<void> => {
  try {
    const token = getStoredToken()

    if (token) {
      // Notify server of logout (optional, may fail silently)
      fetch(`${AUTH_CONFIG.authUrl}/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(() => {
        // Ignore logout API errors - still clear local tokens
      })
    }
  } finally {
    // Always clear tokens locally
    clearStoredTokens()

    // Dispatch logout event for components to listen
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:logout'))
    }
  }
}

/**
 * Refresh authentication token
 */
export const refreshAuthToken = async (): Promise<AuthState | null> => {
  try {
    const refreshToken = getStoredRefreshToken()

    if (!refreshToken) {
      return null
    }

    const response = await fetch(`${AUTH_CONFIG.authUrl}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({ refreshToken })
    })

    if (!response.ok) {
      clearStoredTokens()
      throw new Error(`Token refresh failed: ${response.statusText}`)
    }

    const data = await response.json()
    storeTokens(data.accessToken, data.refreshToken)

    return {
      isLoggedIn: true,
      user: data.user,
      token: data.accessToken,
      refreshToken: data.refreshToken
    }
  } catch (error) {
    console.error('Token refresh error:', error)
    clearStoredTokens()
    return null
  }
}

/**
 * Get stored authentication token
 */
function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null

  try {
    return localStorage.getItem('ligonier_auth_token')
  } catch {
    return null
  }
}

/**
 * Get stored refresh token
 */
function getStoredRefreshToken(): string | null {
  if (typeof window === 'undefined') return null

  try {
    return localStorage.getItem('ligonier_refresh_token')
  } catch {
    return null
  }
}

/**
 * Store authentication tokens securely
 */
function storeTokens(accessToken: string, refreshToken?: string): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem('ligonier_auth_token', accessToken)
    if (refreshToken) {
      localStorage.setItem('ligonier_refresh_token', refreshToken)
    }

    // Dispatch login event
    window.dispatchEvent(new CustomEvent('auth:login', {
      detail: { accessToken, refreshToken }
    }))
  } catch (error) {
    console.error('Failed to store auth tokens:', error)
  }
}

/**
 * Clear stored authentication tokens
 */
function clearStoredTokens(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem('ligonier_auth_token')
    localStorage.removeItem('ligonier_refresh_token')
  } catch (error) {
    console.error('Failed to clear auth tokens:', error)
  }
}

/**
 * Listen for authentication state changes
 */
export function onAuthStateChange(callback: (authState: AuthState | null) => void): () => void {
  if (typeof window === 'undefined') return () => { }

  const handleLogin = () => {
    checkAuthStatus().then(callback)
  }

  const handleLogout = () => {
    callback({
      isLoggedIn: false,
      user: null,
      token: null,
      refreshToken: null
    })
  }

  window.addEventListener('auth:login', handleLogin)
  window.addEventListener('auth:logout', handleLogout)

  // Return cleanup function
  return () => {
    window.removeEventListener('auth:login', handleLogin)
    window.removeEventListener('auth:logout', handleLogout)
  }
}
